import { isEmpty, trim } from 'lodash';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { getAssembliesStatus } from 'src/components/timeRow/TimeRow.helper';
import { PATHS } from 'src/constants/paths';
import { useGetDaypartsByLocationGroup, useGetProjectConfig } from 'src/hooks';
import { useApiErrorNavigator } from 'src/hooks/navigation/useApiErrorNavigator';
import { useGetDefaultAssemblyOfDayparts } from 'src/hooks/useGetDefaultAssemblyOfDayparts';
import {
  IAssembly,
  ILocationGroupTabList,
  IScreenOrientation,
} from 'src/interfaces';
import { lang } from 'src/utils';

import {
  ComponentScheduleDrawerFormValues,
  ComponentScheduleDrawerProps,
} from './ComponentScheduleDrawer.interface';

export interface ILocationScheduleComponentState {
  locationGroupId: string;
  scheduleName: string;
  daypartId: number;
  layoutId: number;
  defaultAssembly: IAssembly;
  locationGroupName: string;
  additionalLocationGroups: boolean;
  orientation?: IScreenOrientation;
  locationGroupList?: ILocationGroupTabList[];
  activeStep?: number;
  selectedLocationGroups?: Set<string | number>;
}

export const useComponentScheduleDrawer = ({
  onToggleDrawer,
  locationGroupName,
  orientation,
}: ComponentScheduleDrawerProps) => {
  const navigate = useNavigate();
  const { locationGroupId } = useParams();

  const { data: projectConfig } = useGetProjectConfig();
  const rbStoreIntegration = projectConfig?.rbStoreIntegration ?? false;

  const {
    data: activeDaypartOptions = [],
    isLoading: isLoadingDayparts,
    error,
  } = useGetDaypartsByLocationGroup(
    [`location_group_id=${locationGroupId}`, 'valid_to.null=true'],
    Boolean(locationGroupId),
  );

  useApiErrorNavigator(error);

  const formMethods = useForm<ComponentScheduleDrawerFormValues>({
    values: { dates: [null, null] },
  });

  const { daypartsWithDefaultAssembly } = useGetDefaultAssemblyOfDayparts(
    locationGroupId!,
    rbStoreIntegration,
  );

  const onHandleCloseDrawer = () => {
    formMethods.reset();
    onToggleDrawer(false);
  };

  const handleFormSubmit = (values: ComponentScheduleDrawerFormValues) => {
    onHandleCloseDrawer();
    //Send & maintain sidebar data to schedule-content
    const selectedDaypart = daypartsWithDefaultAssembly.find(
      (daypart) => daypart.id === values.selectedDaypartId,
    );
    // TODO: The schedule component support the default assembly right now. Update the logic if future development comes
    const { defaultAssembly } = getAssembliesStatus(
      selectedDaypart?.defaultAssembly,
    );

    localStorage.setItem(
      'createScheduleState',
      JSON.stringify({
        locationGroupId,
        scheduleName: values.scheduleName,
        daypartId: values.selectedDaypartId,
        layoutId: defaultAssembly?.layout_id,
        defaultAssembly: defaultAssembly,
        locationGroupName,
        additionalLocationGroups: !!values.additionalLocationGroups,
        orientation,
      } as ILocationScheduleComponentState),
    );

    navigate(PATHS.LOCATIONS_SCHEDULE_COMPONENTS);
  };

  const emptyScheduleNameValidate = (value: string | undefined) => {
    const scheduleName = value;
    const trimmedScheduledName = trim(scheduleName);

    if (isEmpty(trimmedScheduledName)) {
      return lang.scheduleComponent.scheduleNameFieldValidationMessage;
    }

    if (trim(trimmedScheduledName).length > 255) {
      return lang.scheduleComponent.scheduleNameError;
    }
  };

  return {
    formMethods,
    isLoading: isLoadingDayparts,
    activeDaypartOptions,
    emptyScheduleNameValidate,
    handleFormSubmit,
    onHandleCloseDrawer,
  };
};
